import { t, tp } from '../i18n/i18n';
/**
 * Arena rework (2026-08-26) — user report: the PvP arena "works very poorly
 * and is highly confusing".
 *
 * The round, said out loud: who you are waiting for, when the fight starts,
 * how long is left, and how many teams are still alive.
 *
 * None of this was on screen before. The arena's only HUD was the playtest
 * debug overlay, so a player had no way to know the round had begun (it began
 * silently), no way to see the five-minute draw timer they were racing, and no
 * way to tell whether they were the last two standing or one of six. All four
 * are facts the server has always had.
 */

export type ArenaPhase = 'WAITING_FOR_PLAYERS' | 'COUNTDOWN' | 'IN_PROGRESS' | 'ROUND_OVER';

export interface ArenaRoundView {
  phase: ArenaPhase;
  /** Whole seconds until the bell, during COUNTDOWN. */
  countdownS: number;
  /** Whole seconds left in the round, during IN_PROGRESS. */
  remainingS: number;
  teamsAlive: number;
  fightersAlive: number;
}

/** How long "FIGHT!" stays up after the bell. Long enough to read, short enough not to sit in the way. */
const FIGHT_FLASH_MS = 1_200;

export class ArenaRoundBanner {
  private readonly root: HTMLDivElement;
  private readonly headline: HTMLDivElement;
  private readonly clock: HTMLDivElement;
  private readonly standing: HTMLDivElement;
  private lastPhase: ArenaPhase | null = null;
  private fightFlashUntil = 0;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // Top centre, above the reticle and clear of the target frame: the round's
    // state is the one thing you glance at rather than watch.
    //
    // `data-hud` so app.css can move it on a phone (2026-09-08). The objective
    // plaque is top-LEFT and 54vw wide, which on a 390px screen reaches 24px
    // past the centre line — and it sits at z-1000 to this banner's z-890, so
    // it painted straight over the countdown. Verified in a capture: the round
    // was starting in a number the player could not see.
    this.root.dataset.hud = 'round-banner';
    this.root.className = 'fixed z-[890] pointer-events-none select-none left-1/2 -translate-x-1/2 '
      + 'top-[max(0.75rem,env(safe-area-inset-top))] flex flex-col items-center gap-0.5 text-center';

    this.headline = document.createElement('div');
    this.headline.className = 'font-heading tracking-[0.2em] text-[clamp(1rem,3.5vw,1.6rem)] '
      + '[text-shadow:0_2px_6px_#000]';

    this.clock = document.createElement('div');
    this.clock.className = 'font-heading text-[clamp(1.5rem,5vw,2.4rem)] leading-none tabular-nums text-text '
      + '[text-shadow:0_2px_6px_#000]';

    this.standing = document.createElement('div');
    this.standing.className = 'eyebrow eyebrow-dim [text-shadow:0_1px_3px_#000]';

    this.root.append(this.headline, this.clock, this.standing);
    container.appendChild(this.root);
  }

  update(view: ArenaRoundView, nowMs: number): void {
    // The bell is a moment, and a moment needs marking — the transition into
    // IN_PROGRESS is the only thing here that is an event rather than a state.
    if (this.lastPhase === 'COUNTDOWN' && view.phase === 'IN_PROGRESS') this.fightFlashUntil = nowMs + FIGHT_FLASH_MS;
    this.lastPhase = view.phase;

    switch (view.phase) {
      case 'WAITING_FOR_PLAYERS':
        this.headline.textContent = t('Waiting for fighters');
        this.headline.style.color = 'var(--color-text-dim)';
        this.clock.textContent = '';
        this.standing.textContent = t('The pit fills up before the bell');
        break;
      case 'COUNTDOWN':
        this.headline.textContent = t('Round starts in');
        this.headline.style.color = 'var(--color-gold, #d9b45b)';
        this.clock.textContent = String(Math.max(0, view.countdownS));
        this.standing.textContent = this.standingLine(view);
        break;
      case 'IN_PROGRESS':
        if (nowMs < this.fightFlashUntil) {
          this.headline.textContent = t('FIGHT');
          this.headline.style.color = 'var(--color-error, #d94f4f)';
        } else {
          this.headline.textContent = '';
        }
        this.clock.textContent = formatClock(view.remainingS);
        this.standing.textContent = this.standingLine(view);
        break;
      case 'ROUND_OVER':
        // The results overlay owns the screen at this point.
        this.headline.textContent = '';
        this.clock.textContent = '';
        this.standing.textContent = '';
        break;
    }
  }

  /**
   * "4 teams · 6 fighters left", or the version that matters: once it is down
   * to two teams, say so plainly. A last-team-standing mode where you cannot
   * tell you are in the last pair is a mode with no tension in it.
   */
  private standingLine(view: ArenaRoundView): string {
    if (view.teamsAlive === 2) {
      return tp(view.fightersAlive, 'Final two teams · {n} fighter left', 'Final two teams · {n} fighters left');
    }
    return tp(view.fightersAlive, '{teams} teams · {n} fighter left', '{teams} teams · {n} fighters left', { teams: view.teamsAlive });
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
  }

  dispose(): void {
    this.root.remove();
  }
}

/** `m:ss`, floored at zero — a negative clock is a bug the player should never see. */
export function formatClock(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
